import { createLedgerEntry, type LedgerEntry } from "./economy.js";
import type { GameOperation } from "./garage.js";
import type { IdGenerator, OperationId, PurchaseOrderId, UserId } from "./ids.js";
import { purchaseStoreItem, type StoreCatalogItem, type StorePurchaseResult } from "./store.js";

export interface TeleportDestination {
  x: number;
  y: number;
  z: number;
  yaw?: number;
  maxGrowth: number;
  destination: string;
  resolveGround: boolean;
  requiredSpecies?: string;
  scatterRadius?: number;
  minimumScatterRadius?: number;
}

export interface StoreOperationPurchaseResult {
  purchase: StorePurchaseResult;
  operation: GameOperation;
}

export const STORE_TELEPORT_COOLDOWN_MS = 30 * 60 * 1_000;

export function successfulStoreTeleportCooldownEndsAt(
  operations: GameOperation[],
  cooldownMs = STORE_TELEPORT_COOLDOWN_MS
): Date | undefined {
  let latest: Date | undefined;
  for (const operation of operations) {
    if (operation.type !== "store_teleport" || operation.status !== "confirmed" || operation.payload?.friendTeleport === true) continue;
    const explicit = parseDate(operation.result?.cooldownEndsAt);
    const reconciledAt = parseDate(operation.result?.reconciledAt);
    const endsAt = explicit ?? new Date((reconciledAt ?? operation.createdAt).getTime() + cooldownMs);
    if (!latest || endsAt > latest) latest = endsAt;
  }
  return latest;
}

function parseDate(value: unknown): Date | undefined {
  if (typeof value !== "string") return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

export function readTeleportDestination(item: StoreCatalogItem): TeleportDestination {
  if (item.type !== "teleport") throw new Error("Selected store item is not a teleport.");
  const metadata = item.metadata ?? {};
  const x = finiteNumber(metadata.x);
  const y = finiteNumber(metadata.y);
  const z = finiteNumber(metadata.z);
  const yaw = optionalFiniteNumber(metadata.yaw);
  const maxGrowth = finiteNumber(metadata.maxGrowth);
  const destination = typeof metadata.destination === "string" ? metadata.destination.trim() : "";
  const resolveGround = metadata.resolveGround === true;
  const requiredSpecies = typeof item.species === "string" ? item.species.trim() : "";
  const scatterRadius = optionalFiniteNumber(metadata.scatterRadius);
  const minimumScatterRadius = optionalFiniteNumber(metadata.minimumScatterRadius);
  if (x === undefined || y === undefined || z === undefined) throw new Error("Teleport destination coordinates are not configured.");
  if (Math.abs(x) > 2_000_000 || Math.abs(y) > 2_000_000 || Math.abs(z) > 500_000) {
    throw new Error("Teleport destination coordinates are outside the configured safety bounds.");
  }
  if (maxGrowth === undefined || maxGrowth < 1 || maxGrowth > 100) throw new Error("Teleport maximum growth must be between 1 and 100.");
  if (!destination) throw new Error("Teleport destination name is not configured.");
  if (!resolveGround) throw new Error("Teleport destination must use verified ground resolution.");
  if (scatterRadius !== undefined && (scatterRadius < 0 || scatterRadius > 100_000)) throw new Error("Teleport scatter radius must be between 0 and 100000 world units.");
  if (minimumScatterRadius !== undefined && (minimumScatterRadius < 0 || minimumScatterRadius > (scatterRadius ?? 0))) throw new Error("Teleport minimum scatter radius must not exceed its scatter radius.");
  return { x, y, z, ...(yaw === undefined ? {} : { yaw }), maxGrowth, destination, resolveGround,
    ...(requiredSpecies ? { requiredSpecies } : {}),
    ...(scatterRadius === undefined ? {} : { scatterRadius }),
    ...(minimumScatterRadius === undefined ? {} : { minimumScatterRadius }) };
}

export function purchaseTeleport(ids: IdGenerator, input: {
  userId: UserId;
  steam64Id: string;
  liveDinoId: string;
  serverId: string;
  item: StoreCatalogItem;
  requestId?: string;
  chargedPrice?: number;
  availablePoints: number;
  currentGrowth: number;
  currentSpecies?: string;
  expectedPawnAddress: string;
  now: Date;
}): StoreOperationPurchaseResult {
  const destination = readTeleportDestination(input.item);
  const expectedPawnAddress = input.expectedPawnAddress.trim();
  if (!expectedPawnAddress || expectedPawnAddress.length > 64 || !/^(?:0x[0-9a-f]+|\d+)$/i.test(expectedPawnAddress)) {
    throw new Error("Teleport requires an exact live dinosaur identity.");
  }
  if (!Number.isFinite(input.currentGrowth) || input.currentGrowth < 0 || input.currentGrowth > 100) {
    throw new Error("Live dinosaur growth could not be verified.");
  }
  if (input.currentGrowth > destination.maxGrowth) {
    throw new Error(`This teleport is only available at ${destination.maxGrowth}% growth or below.`);
  }
  if (destination.requiredSpecies && !speciesMatches(input.currentSpecies, destination.requiredSpecies)) {
    throw new Error(`This teleport is only available to ${destination.requiredSpecies}.`);
  }
  const purchase = purchaseStoreItem(ids, input);
  const operation: GameOperation = {
    id: (input.requestId ? `operation_${input.requestId}` : ids.next("operation")) as OperationId,
    type: "store_teleport",
    userId: input.userId,
    targetId: purchase.order.id,
    status: "reserved",
    payload: {
      steam64Id: input.steam64Id,
      liveDinoId: input.liveDinoId,
      serverId: input.serverId,
      purchaseOrderId: purchase.order.id,
      catalogItemId: input.item.id,
      itemName: input.item.name,
      price: purchase.order.price,
      destination: destination.destination,
      x: destination.x,
      y: destination.y,
      z: destination.z,
      ...(destination.yaw === undefined ? {} : { yaw: destination.yaw }),
      maxGrowth: destination.maxGrowth,
      ...(destination.resolveGround ? { resolveGround: true } : {}),
      ...(destination.scatterRadius === undefined ? {} : { scatterRadius: destination.scatterRadius }),
      ...(destination.minimumScatterRadius === undefined ? {} : { minimumScatterRadius: destination.minimumScatterRadius }),
      currentGrowth: input.currentGrowth,
      expectedPawnAddress
    },
    createdAt: input.now
  };
  return { purchase, operation };
}

export function createStorePurchaseRefund(ids: IdGenerator, input: {
  userId: UserId;
  orderId: PurchaseOrderId | string;
  amount: number;
  itemName: string;
  now: Date;
}): LedgerEntry {
  return createLedgerEntry(ids, {
    userId: input.userId,
    type: "credit",
    amount: input.amount,
    reason: `Refund: ${input.itemName}`,
    referenceType: "purchase_order_refund",
    referenceId: input.orderId,
    now: input.now
  });
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function optionalFiniteNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  return finiteNumber(value);
}

function normalizeSpecies(value: string | undefined): string {
  return (value ?? "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

function speciesMatches(current: string | undefined, required: string): boolean {
  const normalizedCurrent = normalizeSpecies(current);
  const normalizedRequired = normalizeSpecies(required);
  return Boolean(normalizedCurrent && normalizedRequired)
    && (normalizedCurrent === normalizedRequired || normalizedCurrent.includes(normalizedRequired));
}
